import { LitElement, html, css, nothing } from 'lit';
import { StoreMixin } from '../store.js';
import { api } from '../api.js';
import { loadEstimate } from '../estimate.js';
import { money } from '../lib/format.js';

/**
 * Rate sheets (Estimate Tool 8.11; 2026-09-23) — on the Estimate settings page.
 *
 * One hourly rate per service, and the sheet a set budget is turned into hours through. The
 * standard sheet is Workamajig's own service rates, read by the sync and not editable here; any
 * other sheet is opened as a copy of one and edited in place. One sheet is the default, which is
 * what an estimate uses when none was chosen.
 *
 * Admin to change, as for any setting — a rate changes every budgeted plan cut after it. Anyone
 * who can reach the tool may read: a planner wondering why a budget bought 300 hours should be
 * able to see the rates that did it.
 */
class EstimateRateSheets extends StoreMixin(LitElement) {
  static properties = {
    _sheets: { state: true },
    _open: { state: true },
    _draft: { state: true },
    _newName: { state: true },
    _copyFrom: { state: true },
    _saving: { state: true },
    _error: { state: true },
    _saved: { state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 20px 24px 18px;
    }
    .eyebrow {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    h2 {
      margin: 6px 0 4px;
      font-size: 20px;
      color: var(--ink-strong);
    }
    .lede {
      margin: 0 0 14px;
      font-size: 13px;
      color: var(--ink-muted);
    }
    .sheets {
      display: grid;
      gap: 8px;
    }
    .sheet {
      border: 1px solid var(--border-soft);
      border-radius: 8px;
      padding: 10px 14px;
    }
    .head {
      display: flex;
      align-items: baseline;
      gap: 10px;
      flex-wrap: wrap;
    }
    .head strong {
      font-size: 14px;
      color: var(--ink-strong);
    }
    .tag {
      font-size: 11px;
      color: var(--ink-muted);
      border: 1px solid var(--border-soft);
      border-radius: 999px;
      padding: 1px 8px;
    }
    .spacer {
      flex: 1;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 10px;
      font-size: 12px;
    }
    th,
    td {
      text-align: left;
      padding: 3px 6px;
      border-top: 1px solid var(--border-soft);
    }
    td.num,
    th.num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    input[type='number'] {
      width: 6em;
      text-align: right;
    }
    input[type='text'] {
      min-width: 16em;
    }
    .actions {
      display: flex;
      gap: 10px;
      align-items: center;
      flex-wrap: wrap;
      margin-top: 12px;
    }
    .new {
      display: flex;
      gap: 8px;
      align-items: center;
      flex-wrap: wrap;
      margin-top: 14px;
      padding-top: 12px;
      border-top: 1px dashed var(--border-soft);
    }
    button.link {
      background: none;
      border: 0;
      padding: 0;
      color: var(--ink-strong);
      text-decoration: underline;
      cursor: pointer;
      font: inherit;
      font-size: 12px;
    }
    .error {
      color: var(--status-overloaded, #b3261e);
      font-size: 12px;
    }
    .ok {
      color: var(--ink-muted);
      font-size: 12px;
    }
    .hint {
      font-size: 12px;
      color: var(--ink-muted);
    }
  `;

  constructor() {
    super();
    this._sheets = null;
    this._open = null;
    this._draft = null;
    this._newName = '';
    this._copyFrom = '';
    this._saving = false;
    this._error = null;
    this._saved = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  get _isAdmin() {
    return this.appState.user?.effective_role === 'admin';
  }

  async _load() {
    try {
      const { sheets } = await api.get('/estimate/rate-sheets');
      this._sheets = sheets;
      this._error = null;
    } catch (err) {
      this._error = `Could not load the rate sheets: ${err.message}`;
    }
  }

  _toggle(sheet) {
    if (this._open === sheet.id) {
      this._open = null;
      this._draft = null;
      return;
    }
    this._open = sheet.id;
    this._draft = { name: sheet.name, rates: sheet.rates.map((r) => ({ ...r })) };
    this._saved = false;
  }

  _setRate(code, value) {
    this._draft = {
      ...this._draft,
      rates: this._draft.rates.map((r) =>
        r.service_code === code ? { ...r, hourly_rate: value } : r,
      ),
    };
    this._saved = false;
  }

  async _save(sheet) {
    this._saving = true;
    this._error = null;
    try {
      const body = { name: String(this._draft.name).trim() };
      if (sheet.editable) {
        body.rates = this._draft.rates.map((r) => ({
          service_code: r.service_code,
          hourly_rate: Number(r.hourly_rate) || 0,
        }));
      }
      await api.patch(`/estimate/rate-sheets/${sheet.id}`, body);
      await this._load();
      await loadEstimate({ force: true });
      this._open = null;
      this._draft = null;
      this._saved = true;
    } catch (err) {
      this._error = err.message;
    } finally {
      this._saving = false;
    }
  }

  async _makeDefault(sheet) {
    this._saving = true;
    this._error = null;
    try {
      await api.patch(`/estimate/rate-sheets/${sheet.id}`, { is_default: true });
      await this._load();
      await loadEstimate({ force: true });
    } catch (err) {
      this._error = err.message;
    } finally {
      this._saving = false;
    }
  }

  async _create() {
    const name = String(this._newName).trim();
    if (!name) return;
    this._saving = true;
    this._error = null;
    try {
      const { sheet } = await api.post('/estimate/rate-sheets', {
        name,
        copy_from: this._copyFrom || null,
      });
      this._newName = '';
      await this._load();
      await loadEstimate({ force: true });
      this._toggle(sheet);
    } catch (err) {
      this._error = err.message;
    } finally {
      this._saving = false;
    }
  }

  _sheetRow(sheet) {
    const open = this._open === sheet.id;
    const avg = sheet.rates.length
      ? sheet.rates.reduce((a, r) => a + r.hourly_rate, 0) / sheet.rates.length
      : null;
    return html`<div class="sheet">
      <div class="head">
        <strong>${sheet.name}</strong>
        ${sheet.is_default ? html`<span class="tag">default</span>` : nothing}
        <span class="tag">${sheet.editable ? 'kept here' : 'Workamajig’s service rates'}</span>
        <span class="hint">
          ${sheet.rate_count}
          services${avg !== null ? html`, averaging ${money(avg)} an hour` : nothing}
        </span>
        <span class="spacer"></span>
        ${
          this._isAdmin && !sheet.is_default
            ? html`<button
                class="link"
                ?disabled=${this._saving}
                @click=${() => this._makeDefault(sheet)}
              >
                Make default
              </button>`
            : nothing
        }
        <button class="link" @click=${() => this._toggle(sheet)}>
          ${open ? 'Close' : 'Rates'}
        </button>
      </div>
      ${open ? this._rates(sheet) : nothing}
    </div>`;
  }

  _rates(sheet) {
    const editable = this._isAdmin && sheet.editable;
    const d = this._draft;
    return html`
      ${
        this._isAdmin
          ? html`<div class="actions">
              <label
                >Name
                <input
                  type="text"
                  .value=${d.name}
                  @input=${(e) => (this._draft = { ...d, name: e.target.value })}
              /></label>
            </div>`
          : nothing
      }
      <table>
        <thead>
          <tr>
            <th>Service</th>
            <th></th>
            <th class="num">$ / hour</th>
          </tr>
        </thead>
        <tbody>
          ${d.rates.map(
            (r) =>
              html`<tr>
                <td>${r.service_code}</td>
                <td class="hint">${r.service_name ?? ''}</td>
                <td class="num">
                  ${
                    editable
                      ? html`<input
                          type="number"
                          min="0"
                          step="5"
                          aria-label="Rate for ${r.service_code}"
                          .value=${String(r.hourly_rate)}
                          @input=${(e) => this._setRate(r.service_code, e.target.value)}
                        />`
                      : money(r.hourly_rate)
                  }
                </td>
              </tr>`,
          )}
        </tbody>
      </table>
      ${
        this._isAdmin
          ? html`<div class="actions">
              <button class="primary" ?disabled=${this._saving} @click=${() => this._save(sheet)}>
                ${this._saving ? 'Saving…' : 'Save'}
              </button>
              ${
                sheet.editable
                  ? nothing
                  : html`<span class="hint">
                      These rates are Workamajig’s and move with the sync; only the name can change
                      here. To vary them, open a copy below.
                    </span>`
              }
            </div>`
          : nothing
      }
    `;
  }

  render() {
    if (this._error && !this._sheets)
      return html`<div class="card"><p class="error">${this._error}</p></div>`;
    if (!this._sheets) return html`<div class="card"><p class="hint">Loading…</p></div>`;
    return html`<div class="card">
      <div class="eyebrow">Rate sheets</div>
      <h2>What an hour of each service costs</h2>
      <p class="lede">
        A set budget on an estimate is turned into hours through one of these, and the schedule is
        scaled to come to the budget at its rates. The standard sheet is Workamajig’s own service
        rates; any other is a copy you keep here.
        ${this._isAdmin ? nothing : html`<strong>Changing them is an admin action; you are reading.</strong>`}
      </p>
      <div class="sheets">${this._sheets.map((s) => this._sheetRow(s))}</div>
      ${
        this._isAdmin
          ? html`<div class="new">
              <label>
                New sheet
                <input
                  type="text"
                  placeholder="Name"
                  .value=${this._newName}
                  @input=${(e) => (this._newName = e.target.value)}
                />
              </label>
              <label>
                copied from
                <select @change=${(e) => (this._copyFrom = e.target.value)}>
                  <option value="">the default</option>
                  ${this._sheets.map((s) => html`<option value=${s.id}>${s.name}</option>`)}
                </select>
              </label>
              <button
                ?disabled=${this._saving || !String(this._newName).trim()}
                @click=${() => this._create()}
              >
                Open it
              </button>
            </div>`
          : nothing
      }
      ${this._error ? html`<p class="error">${this._error}</p>` : nothing}
      ${this._saved ? html`<p class="ok">Saved.</p>` : nothing}
    </div>`;
  }
}

customElements.define('estimate-rate-sheets', EstimateRateSheets);
